<template>
	<section class="template-edit">
		<h1 v-if="id" v-translate class="is-size-1">
			Edit template
		</h1>
		<h1 v-else v-translate class="is-size-1">
			New template
		</h1>
		<form ref="form" @submit.prevent="save">
			<div v-if="data">
				<b-field grouped>
					<b-field :label="$gettext('Name')" expanded>
						<b-input v-model="data.name" name="name" required></b-input>
					</b-field>
				</b-field>
				<b-field grouped>
					<b-field :label="$gettext('Platform flavor')" expanded>
						<b-select v-model="data.platformFlavor" expanded>
							<option :value="null">{{ $gettext('None') }}</option>
							<option v-for="flavor in platformFlavors" :key="flavor._id" :value="flavor._id">
								{{ flavor.name }}
							</option>
						</b-select>
					</b-field>
				</b-field>
				<b-field grouped>
					<b-field :label="$gettext('User role')" expanded>
						<b-select v-model="data.role" expanded>
							<option :value="null">{{ $gettext('None') }}</option>
							<option v-for="role in allRoles" :key="role._id" :value="role._id">
								{{ role.name }}
							</option>
						</b-select>
					</b-field>
				</b-field>
				<b-field grouped>
					<b-field :label="$gettext('Language')" expanded>
						<b-select v-model="data.language" expanded>
							<option :value="null">{{ $gettext('None') }}</option>
							<option v-for="language in allLanguages" :key="language.code" :value="language.code">
								{{ language.name }}
							</option>
						</b-select>
					</b-field>
				</b-field>

				<h2 v-translate class="is-size-2 mt-6">Applications</h2>
				<associate
					:name="$gettext('applications')"
					:associated="associated.applications"
					:unassociated="unassociated.applications"
					key-name="_id"
					:display-keys="['name']"
					:search-keys="['name']"
					@change="handleChangeApplications"
				></associate>

				<h2 v-translate class="is-size-2 mt-6">Disciplines</h2>
				<associate
					:name="$gettext('disciplines')"
					:associated="associated.disciplines"
					:unassociated="unassociated.disciplines"
					key-name="_id"
					:display-keys="['name']"
					:search-keys="['name']"
					@change="handleChangeDisciplines"
				></associate>

				<b-loading v-model="isLoading" :is-full-page="false"></b-loading>
			</div>
			<div class="buttons is-pulled-right mt-6">
				<b-button @click="back"><translate>Back to list</translate></b-button>
				<b-button
					v-if="data && access.scopes.includes('user:write')"
					type="is-primary"
					icon-left="content-save"
					@click="save"
				>
					<translate>Save</translate>
				</b-button>
			</div>
		</form>
	</section>
</template>

<script>
import { mapActions, mapState } from 'vuex';
import { pick } from 'ramda';
import { errorToast, successToast } from '@moveup/app-core/mixins';
import { Associate } from '@moveup/app-core/components';
import { fetchAll } from '@/utils/fetchAll';

export default {
	name: 'EditTemplate',
	components: { Associate },
	mixins: [errorToast, successToast],
	data() {
		return {
			id: undefined,
			isLoading: false,
			data: undefined,
			allApplications: [],
			allDisciplines: [],
			allRoles: [],
			allLanguages: [],
			platformFlavors: [],
			associated: {
				applications: [],
				disciplines: [],
			},
			unassociated: {
				applications: [],
				disciplines: [],
			},
		};
	},
	computed: {
		...mapState('auth', ['access']),
	},
	mounted() {
		if (this.$route.params.id) this.id = this.$route.params.id;
		this.loadAsyncData();
	},
	methods: {
		...mapActions('app', [
			'getTemplate', 'createTemplate', 'updateTemplate',
			'getApplications', 'getDisciplines', 'getRoles', 'getPlatformFlavors', 'getLanguages',
		]),
		save() {
			this.$refs.form.reportValidity();
			if (this.$refs.form.checkValidity()) {
				this.data.applications = this.associated?.applications?.map(a => a.name) || [];
				this.data.disciplines = this.associated?.disciplines?.map(d => d._id) || [];

				const propertiesToPick = [
					'_id', 'name', 'platformFlavor', 'role', 'language', 'applications', 'disciplines',
				];
				const patch = pick(propertiesToPick, this.data);

				if (this.id) {
					this.updateTemplate(patch)
						.then(result => {
							this.data = result;
							this.successToast(this.$gettext('Template updated'));
						})
						.catch(this.errorToast);
				} else {
					this.createTemplate(patch)
						.then(result => {
							this.data = result;
							this.successToast(this.$gettext('Template created'));
						})
						.catch(this.errorToast);
				}
			}
		},
		back() {
			this.$router.push('/templates');
		},
		async loadAsyncData() {
			this.isLoading = true;
			const params = {
				sortBy: 'name|asc',
				isService: false,
			};
			if (!this.id) {
				this.data = {
					applications: [],
					disciplines: [],
				};
			}
			const promises = [];
			if (this.id)
				promises.push(
					this.getTemplate(this.id)
						.then(data => {
							this.data = data;
						})
						.catch(err => {
							this.data = undefined;
							this.errorToast(err);
						}),
				);

			promises.push(
				fetchAll(p => this.getApplications(p), params)
					.then(results => {
						this.allApplications = results;
					})
					.catch(err => {
						this.allApplications = [];
						this.errorToast(err);
					}),
			);

			promises.push(
				fetchAll(p => this.getDisciplines(p), { sortBy: 'name|asc' })
					.then(results => {
						this.allDisciplines = results;
					})
					.catch(err => {
						this.allDisciplines = [];
						this.errorToast(err);
					}),
			);

			promises.push(
				fetchAll(p => this.getRoles(p), { sortBy: 'name|asc' })
					.then(results => {
						this.allRoles = results;
					})
					.catch(err => {
						this.allRoles = [];
						this.errorToast(err);
					}),
			);

			promises.push(
				fetchAll(p => this.getPlatformFlavors(p), { sortBy: 'name|asc' })
					.then(results => {
						this.platformFlavors = results;
					})
					.catch(err => {
						this.platformFlavors = [];
						this.errorToast(err);
					}),
			);

			promises.push(
				this.getLanguages()
					.then(data => {
						this.allLanguages = data?.results || [];
					})
					.catch(err => {
						this.allLanguages = [];
						this.errorToast(err);
					}),
			);

			await Promise.all(promises);

			this.unassociated.applications = this.data.applications
				? this.allApplications.filter(item => !this.data?.applications?.includes(item.name))
				: this.allApplications;
			this.associated.applications = (this.data?.applications || [])
				.map(a => this.allApplications.find(aa => aa.name === a))
				.filter(Boolean);

			this.unassociated.disciplines = this.data.disciplines
				? this.allDisciplines.filter(item => !this.data?.disciplines?.includes(item._id))
				: this.allDisciplines;
			this.associated.disciplines = (this.data?.disciplines || [])
				.map(id => this.allDisciplines.find(d => d._id === id))
				.filter(Boolean);

			this.isLoading = false;
		},
		handleChangeApplications({ associated, unassociated }) {
			this.associated.applications = associated;
			this.unassociated.applications = unassociated;
		},
		handleChangeDisciplines({ associated, unassociated }) {
			this.associated.disciplines = associated;
			this.unassociated.disciplines = unassociated;
		},
	},
};
</script>
